import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { select, Store } from '@ngrx/store';

import {
  cloneDeep,
  filter as lodashFilter,
  has,
  includes,
  isEmpty,
  isNil,
  isNull,
  isUndefined,
  map as lodashMap,
  nth
} from 'lodash-es';
import { BehaviorSubject, combineLatest, map, Observable, tap } from 'rxjs';
import { concatMap, filter } from 'rxjs/operators';

import { AddressEnum } from '../../../../../../enums/address';
import { OwnerNameEnum } from '../../../../../../enums/owner-name';
import * as fromWalkingFarmActions from '../../../../../../store/actions/walking-farm.actions';
import * as fromOrganizationSelectors from '../../../../../../store/selectors/organization.selectors';
import * as fromWalkingFarmSelectors from '../../../../../../store/selectors/walking-farm.selectors';
import { WalkingFarmProperty } from '../../../../../../typings/walking-farm';
import { AddressPipe } from '../../../../../pipes/address.pipe';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-walking-farm-view-list',
  standalone: false,
  templateUrl: './list.component.html'
})
export class ListComponent implements OnChanges {
  private activatedRoute = inject(ActivatedRoute);
  private store = inject(Store);
  private addressPipe = inject(AddressPipe);

  @Input() walkingFarmId: string | null = null;
  @Input() propertiesFilterText: string | null = null;

  walkingFarmId$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  walkingFarmProperties$: Observable<WalkingFarmProperty[]>;
  walkingFarmPropertiesFilterText$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  organizationFarmAlertsEnabled$: Observable<boolean>;
  walkingFarmFarmAlertsEnabled$: Observable<boolean>;

  propertiesOrder: string[] = [];
  reorderingDisabled = true;

  defaultBackHref: string | undefined;

  constructor() {
    this.defaultBackHref = this.activatedRoute.snapshot.data['defaultBackHref'] ?? '/';

    this.walkingFarmProperties$ = combineLatest([
      this.walkingFarmId$.pipe(
        filter((id) => !isNil(id)),
        concatMap((id) => this.store.pipe(select(fromWalkingFarmSelectors.selectWalkingFarmProperties(id)))),
        filter((walkingFarmProperties) => !isUndefined(walkingFarmProperties))
      ),
      this.walkingFarmPropertiesFilterText$.asObservable(),
      this.walkingFarmId$.pipe(
        filter((id) => !isNil(id)),
        concatMap((id) =>
          this.store.pipe(select(fromWalkingFarmSelectors.selectWalkingFarmViewFiltersAppliedNumber(id)))
        ),
        filter((walkingFarmProperties) => !isUndefined(walkingFarmProperties))
      )
    ]).pipe(
      map(([properties, filterText, filtersApplied]) =>
        lodashFilter(properties, (property) => {
          let result = true;
          this.reorderingDisabled = true;
          if (!isNull(filterText) && !isEmpty(filterText)) {
            result = false;
            if (includes(this.addressPipe.transform(property).toLowerCase(), filterText.toLowerCase())) {
              result = true;
            } else if (includes(property.A016_OwnerName?.toLowerCase(), filterText.toLowerCase())) {
              result = true;
            } else if (includes(property.A009_OwnerName2?.toLowerCase(), filterText.toLowerCase())) {
              result = true;
            }
          } else if (filtersApplied === 0) {
            this.reorderingDisabled = false;
          }
          return result;
        })
      ),
      tap((properties) => {
        this.propertiesOrder = lodashMap(properties, (item) => `${item.A000_PropertyNumber}`);
      })
    );

    this.walkingFarmFarmAlertsEnabled$ = this.store.pipe(select(fromWalkingFarmSelectors.selectAlertsEnabled));
    this.organizationFarmAlertsEnabled$ = this.store.pipe(select(fromOrganizationSelectors.selectFarmAlertsEnabled));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (has(changes, 'walkingFarmId')) {
      this.walkingFarmId$.next(this.walkingFarmId);
    }
    if (has(changes, 'propertiesFilterText')) {
      this.walkingFarmPropertiesFilterText$.next(this.propertiesFilterText);
    }
  }

  propertiesListTrackBy(_index: number, property: WalkingFarmProperty) {
    return property.A000_PropertyNumber;
  }

  handleReorder(event: CustomEvent) {
    const movedElement = nth(this.propertiesOrder, event.detail.from);
    if (!isNull(this.walkingFarmId) && !isUndefined(movedElement)) {
      const propertiesOrder = cloneDeep(this.propertiesOrder);
      propertiesOrder.splice(event.detail.from, 1);
      propertiesOrder.splice(event.detail.to, 0, movedElement);
      event.detail.complete();
      this.store.dispatch(
        fromWalkingFarmActions.updatePropertiesOrder({
          payload: {
            farmId: this.walkingFarmId,
            order: propertiesOrder
          }
        })
      );
    }
  }

  protected readonly addressType = AddressEnum;
  protected readonly ownerNameType = OwnerNameEnum;
}
